/**
 * Pure decisions behind the platform status screens — planned maintenance, and our backend being
 * unreachable — shared by web and mobile (mobile imports this file through its `@Features` alias).
 * No I/O and no platform globals here; see `platformStatusMonitor.ts`.
 *
 * Maintenance comes from a status document each frontend bucket serves at STATUS_DOCUMENT_PATH.
 * The `cube-maintenance-mute` Lambda publishes it from the SSM maintenance-window register, and it
 * lists only windows declared with the contractual notice — the same windows the SLA report
 * excludes — so a window that does not count as planned never shows the maintenance screen.
 */

export const STATUS_DOCUMENT_PATH = "/status/maintenance.json";

export interface MaintenanceWindow {
  /** Epoch milliseconds. */
  start: number;
  end: number;
  /** The customer-visible reason, as declared on the window (not translated). May be empty. */
  reason: string;
}

export interface StatusDocument {
  maintenance: MaintenanceWindow[];
  /** SHA-256 (hex) of the staff bypass token, or null when bypass is switched off. */
  bypassSha256: string | null;
}

/**
 * What a reachability check found. "offline" means the device's own connection is down: nothing is
 * shown for it — only our side failing ("backend-unreachable") gets a screen.
 */
export type Connectivity = "online" | "backend-unreachable" | "offline";

export type PlatformScreen =
  { kind: "none" } | { kind: "maintenance"; window: MaintenanceWindow } | { kind: "backend-unreachable" };

export interface PlatformView {
  screen: PlatformScreen;
  /** Set when maintenance is in force but this browser holds the staff bypass. */
  staffBypass: MaintenanceWindow | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const SHA256_HEX = /^[0-9a-f]{64}$/;

/** Parses a published status document. Malformed windows are dropped; anything else is null. */
export function parseStatusDocument(body: unknown): StatusDocument | null {
  if (!isRecord(body) || body.version !== 1 || !Array.isArray(body.maintenance)) return null;
  const maintenance = body.maintenance.flatMap((item: unknown): MaintenanceWindow[] => {
    if (!isRecord(item) || typeof item.start !== "string" || typeof item.end !== "string") return [];
    const start = Date.parse(item.start);
    const end = Date.parse(item.end);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
    return [{ start, end, reason: typeof item.reason === "string" ? item.reason.trim() : "" }];
  });
  const hash = body.bypass_sha256;
  return { maintenance, bypassSha256: typeof hash === "string" && SHA256_HEX.test(hash) ? hash : null };
}

/**
 * Where a client that is not served from the frontend host (mobile) finds the status document.
 * Deployments pair `api.<host>` with `<host>`, or serve the API at `<host>/api-server`.
 */
export function statusDocumentUrlFor(backendUrl: string): string | null {
  const match = /^(https?:\/\/)([^/?#]+)(\/api-server)?\/?$/.exec(backendUrl.trim());
  if (!match) return null;
  const [, scheme = "", host = "", apiPath] = match;
  return `${scheme}${apiPath ? host : host.replace(/^api\./, "")}${STATUS_DOCUMENT_PATH}`;
}

/**
 * The window in force at `now`, with its end carried across any window that overlaps or directly
 * follows it — otherwise back-to-back windows would announce a return time that is not the real one.
 */
export function activeMaintenance(windows: readonly MaintenanceWindow[], now: number): MaintenanceWindow | null {
  const current = windows.find((w) => w.start <= now && now < w.end);
  if (!current) return null;
  let end = current.end;
  let extended = true;
  while (extended) {
    extended = false;
    for (const w of windows) {
      if (w.start <= end && w.end > end) {
        end = w.end;
        extended = true;
      }
    }
  }
  return { ...current, end };
}

/** The next instant after `now` at which a window starts or ends, i.e. when the screen may change. */
export function nextBoundary(windows: readonly MaintenanceWindow[], now: number): number | null {
  let next: number | null = null;
  for (const w of windows) {
    for (const edge of [w.start, w.end]) {
      if (edge > now && (next === null || edge < next)) next = edge;
    }
  }
  return next;
}

/**
 * Tells "our backend is down" from "the user is offline". A device's own online flag is only
 * trustworthy when it says offline, so the positive case is decided by whether our static host —
 * a different service from the backend — still answers.
 */
export function classifyConnectivity(probe: {
  deviceOffline: boolean;
  backendAnswered: boolean;
  staticHostAnswered: boolean;
}): Connectivity {
  if (probe.backendAnswered) return "online";
  if (probe.deviceOffline || !probe.staticHostAnswered) return "offline";
  return "backend-unreachable";
}

export function decideView(input: {
  windows: readonly MaintenanceWindow[];
  now: number;
  bypassed: boolean;
  backendUnreachable: boolean;
}): PlatformView {
  const window = activeMaintenance(input.windows, input.now);
  if (window && !input.bypassed) return { screen: { kind: "maintenance", window }, staffBypass: null };
  const screen: PlatformScreen = input.backendUnreachable ? { kind: "backend-unreachable" } : { kind: "none" };
  return { screen, staffBypass: window };
}

/**
 * Server clock minus local clock, from a response's Date and Age headers. Maintenance starts and ends
 * at fixed instants, so a device whose clock is wrong would otherwise block its user early or late.
 */
export function clockOffset(dateHeader: string | null, ageHeader: string | null, localNow: number): number {
  const served = dateHeader ? Date.parse(dateHeader) : Number.NaN;
  if (!Number.isFinite(served)) return 0;
  const age = Number(ageHeader);
  return served + (Number.isFinite(age) && age > 0 ? age * 1000 : 0) - localNow;
}

/**
 * Delay before the next reconnection check: 5 s doubling to a 30 s ceiling, jittered ±20 % so every
 * open client does not hit a recovering backend in the same instant.
 */
export function recheckDelay(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(5_000 * 2 ** attempt, 30_000);
  return Math.round(base * (0.8 + random() * 0.4));
}

/** An instant as the user reads it: weekday, date, time and zone, in their language. */
export function formatMaintenanceTime(epochMs: number, language: string): string {
  try {
    return new Intl.DateTimeFormat(language, {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(epochMs);
  } catch {
    // An unknown language tag, or an engine without full Intl (older Hermes).
    return new Date(epochMs).toLocaleString();
  }
}
